import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, CurrencyDollar, ArrowRight, Receipt } from "@phosphor-icons/react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { fmtUsd } from "@/lib/format";

const TIER_PERKS = {
  individual: ["1 seat", "Unlimited projects", "EPD library", "AI Copilot"],
  organization: ["4 seats", "Org workspace", "Role-based access", "Procurement Lock-in"],
};

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/me"),
      api.get("/billing/history"),
    ]);
    setPlans(a.data.items);
    setCurrent(b.data.subscription);
    setHistory(c.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/create-order", { plan_id: planId });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "GPI.CarbonIQ",
        description: data.plan.name,
        order_id: data.order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan_id: planId,
            });
            toast.success("Subscription activated");
            await load();
          } catch (err) {
            toast.error(formatErr(err));
          }
        },
        modal: {
          ondismiss: () => setBusy(""),
        },
      };
      if (!window.Razorpay) {
        toast.error("Razorpay SDK not loaded. Please refresh the page.");
        setBusy("");
        return;
      }
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy("");
    }
  };

  const individual = plans.filter((p) => p.tier === "individual");
  const organization = plans.filter((p) => p.tier === "organization");

  const renderTier = (label, list) => (
    <div className="border theme-border theme-surface p-6">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">{label}</div>
          <h3 className="font-serif-display text-2xl theme-ink mt-1">
            {label === "individual" ? "Individual" : "Organisation · 4 seats"}
          </h3>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {list.map((p) => {
          const isCurrent = current?.plan_id === p.id && current?.status === "active";
          return (
            <button
              key={p.id}
              data-testid={`subscribe-${p.id}`}
              onClick={() => subscribe(p.id)}
              disabled={busy === p.id || isCurrent}
              className={`relative text-left p-4 border rounded-sm transition-colors disabled:opacity-60 ${
                p.popular ? "border-gold bg-gold/5" : "theme-border hover:border-gold/40"
              } ${isCurrent ? "ring-1 ring-eco" : ""}`}
            >
              {p.popular && (
                <span className="absolute -top-2 right-2 text-[10px] tracking-widest uppercase text-gold theme-bg px-2">
                  Best value
                </span>
              )}
              {isCurrent && (
                <span className="absolute -top-2 left-2 text-[10px] tracking-widest uppercase text-eco theme-bg px-2">
                  Active
                </span>
              )}
              <div className="overline">{p.cycle}</div>
              <div className="font-mono-data text-3xl theme-ink mt-1">${p.amount_usd}</div>
              <div className="text-xs theme-ink-dim mt-1">
                {p.discount_pct > 0 ? `Save ${p.discount_pct}%` : "every cycle"}
              </div>
              {busy === p.id && (
                <div className="text-[11px] text-gold mt-2">Opening Razorpay…</div>
              )}
            </button>
          );
        })}
      </div>
      <ul className="mt-5 space-y-2 text-sm">
        {TIER_PERKS[list[0]?.tier]?.map((perk) => (
          <li key={perk} className="flex items-center gap-2 theme-ink-muted">
            <CheckCircle size={14} weight="fill" className="text-eco" /> {perk}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Billing"
        title="Plans & subscription"
        subtitle="USD billing via Razorpay. Cancel anytime. Current plan is highlighted."
        guideId="billing"
      />

      {current?.status === "active" && (
        <section className="px-8 pt-8">
          <div className="border border-eco/30 bg-eco/5 p-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle size={22} weight="fill" className="text-eco" />
              <div>
                <div className="overline">Active subscription</div>
                <div className="font-serif-display text-xl theme-ink mt-0.5">{current.plan_name}</div>
                <div className="text-xs theme-ink-muted mt-1">
                  Activated {current.activated_at?.slice(0, 10)} · Renews/expires {current.period_end?.slice(0, 10)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono-data text-2xl text-eco">{fmtUsd(current.amount_usd)}</div>
              <div className="text-[11px] theme-ink-dim uppercase tracking-widest">{current.cycle}</div>
            </div>
          </div>
        </section>
      )}

      <section className="px-8 pt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {renderTier("individual", individual)}
        {renderTier("organization", organization)}
      </section>

      <section className="px-8 pt-8 pb-12">
        <div className="border theme-border theme-surface">
          <div className="px-6 py-4 border-b theme-border flex items-center gap-2">
            <Receipt size={16} className="text-gold" />
            <div>
              <div className="overline">Payment history</div>
              <div className="font-serif-display text-xl theme-ink">{history.length} record{history.length === 1 ? "" : "s"}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                <tr>
                  <th className="text-left px-6 py-3">Date</th>
                  <th className="text-left px-3 py-3">Plan</th>
                  <th className="text-left px-3 py-3">Cycle</th>
                  <th className="text-right px-3 py-3">Amount</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-left px-3 py-3">Razorpay ID</th>
                </tr>
              </thead>
              <tbody>
                {history.map((s) => (
                  <tr key={s.id} className="border-t theme-border">
                    <td className="px-6 py-3 font-mono-data theme-ink">{s.created_at?.slice(0, 10)}</td>
                    <td className="px-3 theme-ink-muted">{s.plan_name}</td>
                    <td className="px-3 theme-ink-muted">{s.cycle}</td>
                    <td className="px-3 py-3 text-right font-mono-data theme-ink">{fmtUsd(s.amount_usd)}</td>
                    <td className="px-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                        s.status === "active" ? "text-eco border border-eco/30" :
                        s.status === "pending" ? "text-warn border border-warn/30" :
                        "theme-ink-dim border theme-border"
                      }`}>{s.status}</span>
                    </td>
                    <td className="px-3 font-mono-data theme-ink-dim text-xs">{s.razorpay_payment_id || s.razorpay_order_id || "—"}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-10 text-center theme-ink-dim">No transactions yet — pick a plan above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
